import React, { FC, useState } from 'react';
import { Card, CardContent, TextField, Button, RadioGroup, FormControlLabel, Radio, Typography, CircularProgress, makeStyles } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import VideoPlayerDemo from './VideoPlayerDemo';
import { fetchPlayFactoryVideo, isPlayFactoryAvailable, VideoData } from '../../utils/playFactoryApi';
import videoDataFromFile from '../../context/video.json';

type VideoSource = 'file' | 'playfactory';

const DEFAULT_CONTENT_ID = '';
const DEFAULT_CONTENT_TYPE = 'EVENT';
const DEFAULT_CONTENT_SELECTOR = 'EME-CENC-HLS';

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3)
	},
	sourceSelector: {
		marginBottom: theme.spacing(3),
		padding: theme.spacing(2),
		backgroundColor: theme.palette.background.paper,
		borderRadius: theme.shape.borderRadius,
		border: `1px solid ${theme.palette.divider}`
	},
	contentIdInput: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(1)
	},
	loadButton: {
		marginTop: theme.spacing(1)
	}
}));

const VideoDemo: FC = () => {
	const classes = useStyles();
	const [videoSource, setVideoSource] = useState<VideoSource>('file');
	const [contentId, setContentId] = useState(DEFAULT_CONTENT_ID);
	const [contentType, setContentType] = useState(DEFAULT_CONTENT_TYPE);
	const [contentSelector, setContentSelector] = useState(DEFAULT_CONTENT_SELECTOR);
	const [videoData, setVideoData] = useState<VideoData>(videoDataFromFile as VideoData);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleLoadVideo = async () => {
		if (videoSource === 'playfactory') {
			if (!contentId.trim()) {
				setError('Content ID is required');
				return;
			}
			
			setLoading(true);
			setError(null);
			
			try {
				const data = await fetchPlayFactoryVideo(contentId.trim(), contentType, contentSelector);
				setVideoData(data);
			} catch (err) {
				setError(err instanceof Error ? err.message : 'Failed to load video from Play Factory');
				console.error('Error loading video:', err);
			} finally {
				setLoading(false);
			}
		} else {
			setVideoData(videoDataFromFile as VideoData);
			setError(null);
		}
	};

	const handleSourceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setVideoSource(event.target.value as VideoSource);
		setError(null);
	};

	return (
		<div className={classes.root}>
			<Card>
				<CardContent>
					<div className={classes.sourceSelector}>
						<Typography variant="h6" gutterBottom>
							Video Source
						</Typography>
						
						<RadioGroup value={videoSource} onChange={handleSourceChange}>
							<FormControlLabel 
								value="file" 
								control={<Radio color="primary" />} 
								label="Load from file (video.json)" 
							/>
							<FormControlLabel 
								value="playfactory" 
								control={<Radio color="primary" />} 
								label="Fetch from Play Factory" 
								disabled={!isPlayFactoryAvailable()}
							/>
						</RadioGroup>

						{!isPlayFactoryAvailable() && (
							<Alert severity="warning" style={{ marginTop: 8 }}>
								Play Factory integration is not available. Backend proxy endpoint is required.
							</Alert>
						)}

						{videoSource === 'playfactory' && (
							<>
								<TextField
									className={classes.contentIdInput}
									fullWidth
									label="Content ID"
									value={contentId}
									onChange={(e) => setContentId(e.target.value)}
									placeholder="e.g., e5137253-aa88-308a-af0b-73f386230aeb"
									variant="outlined"
									size="small"
									required
								/>
								<TextField
									className={classes.contentIdInput}
									fullWidth
									label="Content Type"
									value={contentType}
									onChange={(e) => setContentType(e.target.value)}
									placeholder="EVENT"
									variant="outlined"
									size="small"
                                    required
                                    InputProps={{ readOnly: true }}
								/>
								<TextField
									className={classes.contentIdInput}
									fullWidth
									label="Content Selector"
									value={contentSelector}
									onChange={(e) => setContentSelector(e.target.value)}
									placeholder="EME-CENC-HLS"
									variant="outlined"
									size="small"
                                    required
									InputProps={{ readOnly: true }}
								/>
								<Button
									className={classes.loadButton}
									variant="contained"
									color="primary"
									onClick={handleLoadVideo}
									disabled={loading || !contentId.trim()}
									startIcon={loading ? <CircularProgress size={20} /> : null}
								>
									{loading ? 'Loading...' : 'Fetch Video'}
								</Button>
							</>
						)}

						{videoSource === 'file' && (
							<Button
								className={classes.loadButton}
								variant="contained"
								color="primary"
								onClick={handleLoadVideo}
							>
								Load from File
							</Button>
						)}

						{error && (
							<Alert severity="error" style={{ marginTop: 16 }}>
								<AlertTitle>Error</AlertTitle>
								{error}
							</Alert>
						)}
					</div>
				</CardContent>
			</Card>

			<VideoPlayerDemo videoData={videoData} />
		</div>
	);
};

export default VideoDemo;
